import { useRef, type ReactNode, useEffect } from "react";
import type { Table } from "@tanstack/react-table";
import { TableToolbar } from "./TableToolbar";
import { RemoteTablePagination } from "./RemoteTablePagination";
import type { VisibilityState } from "@tanstack/react-table";

export interface ColumnConfig {
  id: string;
  label: string;
}

export interface RemoteTableLayoutProps<TData> {
  table: Table<TData>;
  toolbar: {
    columnVisibility: VisibilityState;
    onColumnVisibilityChange: (visibility: VisibilityState) => void;
    availableColumns: readonly ColumnConfig[];
    leftActions?: ReactNode;
    actions?: ReactNode;
  };
  filtersPane?: ReactNode;
  tableContent: ReactNode;
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    allowedPageSizes: readonly number[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    itemLabel?: string;
  };
  error?: Error | null;
  isLoading?: boolean;
  showPagination?: boolean;
  onScrollContainerRef?: (ref: React.RefObject<HTMLDivElement | null>) => void;
}

export function RemoteTableLayout<TData>({
  table,
  toolbar,
  filtersPane,
  tableContent,
  pagination,
  error,
  isLoading = false,
  showPagination = true,
  onScrollContainerRef,
}: RemoteTableLayoutProps<TData>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onScrollContainerRef) {
      onScrollContainerRef(scrollContainerRef);
    }
  }, [onScrollContainerRef]);

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden min-w-0 w-full max-w-full">
      <div className="relative flex flex-1 flex-col overflow-hidden min-w-0 min-h-0 w-full max-w-full">
        <TableToolbar
          columnVisibility={toolbar.columnVisibility}
          onColumnVisibilityChange={toolbar.onColumnVisibilityChange}
          columns={toolbar.availableColumns}
          leftActions={toolbar.leftActions}
          actions={toolbar.actions}
        />

        {filtersPane && (
          <div className="border-b border-grid-200 bg-white px-4 py-3 shrink-0 overflow-hidden animate-in slide-in-from-top-2 fade-in duration-200">
            {filtersPane}
          </div>
        )}

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-auto bg-grid-50 p-3 min-h-0 min-w-0 w-full max-w-full"
          style={showPagination && pagination ? { paddingBottom: "4rem" } : {}}
        >
          {error ? (
            <div className="flex flex-1 items-center justify-center py-12">
              <div className="text-center">
                <p className="text-sm text-ink-900 font-medium">
                  Error loading data
                </p>
                <p className="text-sm text-ink-500 mt-1">
                  {error instanceof Error
                    ? error.message
                    : "An unexpected error occurred"}
                </p>
              </div>
            </div>
          ) : isLoading ? (
            <div className="py-12 text-center text-sm text-ink-500">
              Loading...
            </div>
          ) : (
            tableContent
          )}
        </div>

        {showPagination && pagination && !error && (
          <RemoteTablePagination
            table={table}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            totalCount={pagination.totalCount}
            allowedPageSizes={pagination.allowedPageSizes}
            onPageChange={pagination.onPageChange}
            onPageSizeChange={pagination.onPageSizeChange}
            itemLabel={pagination.itemLabel}
          />
        )}
      </div>
    </div>
  );
}
